/**
 * CountryLabels - Billboarded text labels for country names.
 *
 * Renders country names as 3D text that always faces the camera,
 * positioned at each country's centroid.
 */

import { useMemo } from "react";

import { GLOBE_CONFIG } from "@/domain/globe/constants";
import { isCountryVisibleAtZoom } from "@/domain/globe/constants/labelVisibility";
import { Html } from "@react-three/drei";

import type { CountryData } from '@/domain/globe/types/features'

interface CountryLabelsProps {
  /** Array of country data with names and bounds */
  countries: CountryData[]
  /** Camera center latitude */
  centerLat: number
  /** Camera center longitude */
  centerLng: number
  /** Current zoom level (affects which labels are shown) */
  zoom: number
}

/**
 * Convert lat/lng to 3D world position.
 * Uses the same coordinate system as CountryGlobe.
 */
function latLngToWorldPos(lat: number, lng: number, radius: number): [number, number, number] {
  const latRad = (lat * Math.PI) / 180
  const lngRad = (lng * Math.PI) / 180
  return [
    radius * Math.cos(latRad) * Math.sin(lngRad),
    radius * Math.sin(latRad),
    -radius * Math.cos(latRad) * Math.cos(lngRad),
  ]
}

/**
 * Calculate centroid from polygon exterior ring.
 * More accurate than bounding box for irregularly shaped countries.
 */
function calculatePolygonCentroid(exterior: number[][]): [number, number] {
  let lngSum = 0
  let latSum = 0
  const count = exterior.length - 1 // Exclude closing point (same as first)

  for (let i = 0; i < count; i++) {
    lngSum += exterior[i][0]
    latSum += exterior[i][1]
  }

  return [latSum / count, lngSum / count]
}

/**
 * Get the largest polygon (by point count) - typically the mainland.
 */
function getLargestPolygon(country: CountryData): number[][] {
  let largest = country.polygons[0].exterior
  let maxPoints = largest.length

  for (const polygon of country.polygons) {
    if (polygon.exterior.length > maxPoints) {
      maxPoints = polygon.exterior.length
      largest = polygon.exterior
    }
  }

  return largest
}

/**
 * Calculate country centroid from the largest polygon (mainland).
 */
function getCountryCentroid(country: CountryData): [number, number] {
  const largestPolygon = getLargestPolygon(country)
  return calculatePolygonCentroid(largestPolygon)
}

/**
 * Calculate angular distance between two lat/lng points (in degrees).
 * Uses spherical law of cosines.
 */
function angularDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const lat1Rad = (lat1 * Math.PI) / 180
  const lat2Rad = (lat2 * Math.PI) / 180
  const dLng = ((lng2 - lng1) * Math.PI) / 180

  const cosAngle =
    Math.sin(lat1Rad) * Math.sin(lat2Rad) + Math.cos(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLng)

  // Clamp to avoid floating point errors with acos
  return (Math.acos(Math.max(-1, Math.min(1, cosAngle))) * 180) / Math.PI
}

/**
 * CountryLabels component - renders billboarded text for all countries.
 * Uses Html for constant screen-space size regardless of zoom.
 * Only shows labels within visible range of camera center.
 */
export function CountryLabels({ countries, centerLat, centerLng, zoom }: CountryLabelsProps) {
  // Position labels closer to camera than globe surface (concave hemisphere)
  const labelRadius = GLOBE_CONFIG.SPHERE_RADIUS - GLOBE_CONFIG.LABEL_RADIUS_OFFSET

  // Memoize label data to avoid recalculating on every render
  const labelData = useMemo(() => {
    return countries.map((country) => {
      const [lat, lng] = getCountryCentroid(country)
      const position = latLngToWorldPos(lat, lng, labelRadius)
      return { iso2: country.iso2, name: country.name, position, lat, lng }
    })
  }, [countries, labelRadius])

  // Filter to only visible labels (by angular distance and zoom-based visibility)
  const visibleLabels = labelData.filter(({ iso2, lat, lng }) => {
    const distance = angularDistance(centerLat, centerLng, lat, lng)
    if (distance > GLOBE_CONFIG.LABEL_MAX_VISIBLE_ANGLE) return false
    return isCountryVisibleAtZoom(iso2, zoom)
  })

  return (
    <>
      {visibleLabels.map(({ iso2, name, position }) => (
        <Html
          key={iso2}
          position={position}
          center
          zIndexRange={[10, 10]}
          style={{
            color: 'white',
            fontSize: '12px',
            fontWeight: 500,
            textTransform: 'uppercase',
            textShadow: '0 0 3px black, 0 0 3px black, 0 0 3px black',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        >
          {name}
        </Html>
      ))}
    </>
  )
}
